import { useState, useEffect } from 'react'

export interface PricingData {
  countryCode: string
  currentPrice: string
  comparePrice: string
  currencyCode: string
  loading: boolean
}

interface CountryPricing {
  current: number
  compare: number
  currency: string
  locale: string
  decimals: boolean
}

const PRICING_MAP: Record<string, CountryPricing> = {
  CL: {
    current: 13499,
    compare: 41499,
    currency: 'CLP',
    locale: 'es-CL',
    decimals: false,
  },
  AR: {
    current: 20999,
    compare: 65999,
    currency: 'ARS',
    locale: 'es-AR',
    decimals: false,
  },
  CO: {
    current: 56999,
    compare: 176999,
    currency: 'COP',
    locale: 'es-CO',
    decimals: false,
  },
  MX: {
    current: 269,
    compare: 829,
    currency: 'MXN',
    locale: 'es-MX',
    decimals: false,
  },
  UY: {
    current: 599,
    compare: 1849,
    currency: 'UYU',
    locale: 'es-UY',
    decimals: false,
  },
  ES: {
    current: 12.99,
    compare: 40.99,
    currency: 'EUR',
    locale: 'es-ES',
    decimals: true,
  },
  US: {
    current: 14.99,
    compare: 47,
    currency: 'USD',
    locale: 'en-US',
    decimals: true,
  },
}

const DEFAULT_PRICING = PRICING_MAP['US']

function formatPrice(amount: number, pricing: CountryPricing): string {
  try {
    return new Intl.NumberFormat(pricing.locale, {
      style: 'currency',
      currency: pricing.currency,
      minimumFractionDigits: pricing.decimals ? 2 : 0,
      maximumFractionDigits: pricing.decimals ? 2 : 0,
    }).format(amount)
  } catch {
    return `${pricing.currency} ${amount}`
  }
}

export function usePricing(): PricingData {
  const [data, setData] = useState<PricingData>({
    countryCode: 'US',
    currentPrice: formatPrice(DEFAULT_PRICING.current, DEFAULT_PRICING),
    comparePrice: formatPrice(DEFAULT_PRICING.compare, DEFAULT_PRICING),
    currencyCode: DEFAULT_PRICING.currency,
    loading: true,
  })

  useEffect(() => {
    fetch('https://ipapi.co/json/')
      .then((res) => res.json())
      .then((json) => {
        const code: string = json.country_code || 'US'
        const pricing = PRICING_MAP[code] ?? DEFAULT_PRICING
        setData({
          countryCode: code,
          currentPrice: formatPrice(pricing.current, pricing),
          comparePrice: formatPrice(pricing.compare, pricing),
          currencyCode: pricing.currency,
          loading: false,
        })
      })
      .catch(() => {
        const pricing = DEFAULT_PRICING
        setData({
          countryCode: 'US',
          currentPrice: formatPrice(pricing.current, pricing),
          comparePrice: formatPrice(pricing.compare, pricing),
          currencyCode: pricing.currency,
          loading: false,
        })
      })
  }, [])

  return data
}
